/** Nutrition Facts label (FDA-style panel) — data + Daily Value helpers. */

const FDA_DV = {
  energy_kcal: 2000,
  total_fat_g: 78,
  saturated_fat_g: 20,
  trans_fat_g: 0,
  cholesterol_mg: 300,
  sodium_mg: 2300,
  carbohydrate_g: 275,
  dietary_fiber_g: 28,
  total_sugars_g: 0,
  added_sugars_g: 50,
  protein_g: 0,
  vitamin_d_mcg: 20,
  calcium_mg: 1300,
  iron_mg: 18,
  potassium_mg: 4700,
};

export const NF_FOOTNOTE =
  '* The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes to a daily diet. 2,000 calories a day is used for general nutrition advice.';

export function formatNutritionValue(value, int) {
  if (value == null || value === '') return '—';
  if (int) return String(Math.round(Number(value)));
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

export function formatGrams(value) {
  return `${formatNutritionValue(value, false)}g`;
}

export function formatMg(value) {
  return `${formatNutritionValue(value, false)}mg`;
}

export function formatMcg(value) {
  return `${formatNutritionValue(value, true)}mcg`;
}

export function formatDvPercent(field, value) {
  const dv = FDA_DV[field];
  if (dv == null || value == null) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  if (dv <= 0) return 0;
  return Math.round((n / dv) * 100);
}

/** Classic catalog JSON → canonical per-100g fields. */
export function normalizeClassicPer100g(per100g) {
  if (!per100g) return null;
  const base =
    per100g.energy_kcal != null
      ? per100g
      : {
          energy_kcal: per100g.kcal,
          protein_g: per100g.protein,
          carbohydrate_g: per100g.carb,
          total_sugars_g: per100g.sugar,
          added_sugars_g: per100g.added_sugar ?? per100g.sugar,
          total_fat_g: per100g.fat,
          saturated_fat_g: per100g.sat_fat,
          trans_fat_g: per100g.trans_fat,
          sodium_mg: per100g.sodium,
        };

  return {
    energy_kcal: base.energy_kcal ?? 0,
    total_fat_g: base.total_fat_g ?? 0,
    saturated_fat_g: base.saturated_fat_g ?? 0,
    trans_fat_g: base.trans_fat_g ?? 0,
    cholesterol_mg: base.cholesterol_mg ?? 0,
    sodium_mg: base.sodium_mg ?? 0,
    carbohydrate_g: base.carbohydrate_g ?? 0,
    dietary_fiber_g: base.dietary_fiber_g ?? 0,
    total_sugars_g: base.total_sugars_g ?? 0,
    added_sugars_g: base.added_sugars_g ?? 0,
    protein_g: base.protein_g ?? 0,
    vitamin_d_mcg: base.vitamin_d_mcg ?? 0,
    calcium_mg: base.calcium_mg ?? 0,
    iron_mg: base.iron_mg ?? 0,
    potassium_mg: base.potassium_mg ?? 0,
  };
}

export function scaleNutritionValues(per100g, weightG) {
  const base = normalizeClassicPer100g(per100g);
  if (!base || !weightG || weightG <= 0) return null;
  const factor = weightG / 100;
  const scaled = {};
  for (const [key, val] of Object.entries(base)) {
    const n = Number(val);
    if (!Number.isFinite(n)) continue;
    if (key === 'energy_kcal') scaled[key] = Math.round(n * factor);
    else if (key.endsWith('_mg') || key.endsWith('_mcg')) scaled[key] = Math.round(n * factor * 10) / 10;
    else scaled[key] = Math.round(n * factor * 10) / 10;
  }
  return scaled;
}

export function resolveNutritionLabelProps({
  nutrition,
  per100g: per100gProp,
  perServing: perServingProp,
  servingLabel: servingLabelProp,
  flavorName,
}) {
  const per100 = normalizeClassicPer100g(nutrition?.per_100g || per100gProp);
  if (!per100) return null;

  const weightG = nutrition?.serving_weight_g || 0;
  const servingLabel =
    nutrition?.serving_label ||
    servingLabelProp ||
    (weightG ? `${weightG} g` : '100g');
  const perServe =
    nutrition?.per_serving ||
    perServingProp ||
    (weightG > 0 && weightG !== 100 ? scaleNutritionValues(per100, weightG) : null);

  const useServing = Boolean(perServe && weightG > 0 && weightG !== 100);
  const values = useServing ? perServe : per100;
  const servingSizeText = useServing ? servingLabel.replace(/\s+/g, '') : '100g';

  const displayFlavor =
    flavorName || nutrition?.flavor_display || nutrition?.flavor_key?.replace(/ Cake$/, '') || '';

  return {
    per100,
    values,
    servingSizeText,
    useServing,
    displayFlavor,
  };
}

/** @deprecated Use formatDvPercent — kept for callers using old name. */
export function nutritionRdaPercent(per100g) {
  const p = normalizeClassicPer100g(per100g);
  if (!p) return {};
  return {
    energy: formatDvPercent('energy_kcal', p.energy_kcal),
    protein: formatDvPercent('protein_g', p.protein_g),
    carbohydrate: formatDvPercent('carbohydrate_g', p.carbohydrate_g),
    total_sugars: formatDvPercent('total_sugars_g', p.total_sugars_g),
    added_sugars: formatDvPercent('added_sugars_g', p.added_sugars_g),
    total_fat: formatDvPercent('total_fat_g', p.total_fat_g),
    saturated_fat: formatDvPercent('saturated_fat_g', p.saturated_fat_g),
    trans_fat: formatDvPercent('trans_fat_g', p.trans_fat_g),
    sodium: formatDvPercent('sodium_mg', p.sodium_mg),
  };
}

export const NF_LABEL_CSS = `
.nf-label {
  box-sizing: border-box;
  max-width: 280px;
  padding: 6px 8px 8px;
  border: 1px solid #000;
  background: #fff;
  color: #000;
  font-family: Helvetica, Arial, "Liberation Sans", sans-serif;
  font-size: 12px;
  line-height: 1.2;
  -webkit-font-smoothing: antialiased;
}
.nf-title {
  font-size: 28px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.02em;
}
.nf-serving {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 700;
}
.nf-bar-thick {
  height: 8px;
  margin: 4px 0 2px;
  background: #000;
}
.nf-bar-medium {
  height: 4px;
  margin: 2px 0;
  background: #000;
}
.nf-amount {
  font-size: 11px;
  font-weight: 700;
}
.nf-calories-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin: 2px 0;
  font-weight: 800;
}
.nf-calories-label {
  font-size: 22px;
  line-height: 1;
}
.nf-calories-value {
  font-size: 30px;
  line-height: 1;
  letter-spacing: -0.02em;
}
.nf-dv-head {
  display: flex;
  justify-content: flex-end;
  padding: 2px 0;
  font-size: 11px;
  font-weight: 800;
  border-bottom: 1px solid #000;
}
.nf-row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 2px 0;
  border-bottom: 1px solid #000;
  font-size: 12px;
}
.nf-row-bold {
  font-weight: 800;
}
.nf-row-indent {
  padding-left: 14px;
  font-weight: 400;
}
.nf-row-indent2 {
  padding-left: 28px;
  font-weight: 400;
}
.nf-row em {
  font-style: italic;
  font-weight: 400;
}
.nf-row-pct {
  font-weight: 800;
  white-space: nowrap;
}
.nf-micro {
  font-size: 12px;
  font-weight: 400;
}
.nf-footnote {
  margin: 4px 0 0;
  font-size: 9px;
  line-height: 1.25;
  font-weight: 400;
}
`;
